import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useMappingProjectSessionStore } from './useMappingProjectSessionStore';
import { formatMissingParameterPreview } from '../lib/formatMissingParameterPreview';
import { resolveMappingProfileAutoRepairGate } from '../lib/mappingProfileAutoRepairGate';
import { markMappingRepairVerifiedPresent } from '../lib/mappingProfileProjectTrust';
import { useMappingProjectReadiness } from './useMappingProjectReadiness';
import { useRepairSetupConfirmation } from './useRepairSetupConfirmation';
import { useUIStore } from '../../../shared/stores/useUIStore';
import { normalizeMappingProjectKey } from '../../../shared/lib/mappingProjectIdentity';
import type { useProjectProfileGate } from './useProjectProfileGate';
import type { ScopeEditorPersistenceState } from '../lib/scopeEditorPersistence';
import type {
  AdditionalCategoriesConfiguration,
  BoltConfiguration,
  ConnectorBindingConfiguration,
  MenuConfiguration,
  ObjectType,
  ProcessPropertiesConfiguration,
} from '../../../shared/types';

type ProjectProfileGate = ReturnType<typeof useProjectProfileGate>;

export interface UseRepairConsentGateArgs {
  activeUserId: string | null;
  activeProjectSessionKey: ProjectProfileGate['activeProjectSessionKey'];
  resolvedProfileKey: ProjectProfileGate['resolvedProfileKey'];
  resolvedProfileName: ProjectProfileGate['resolvedProfileName'];
  profileConfirmedForCurrentSession: ProjectProfileGate['profileConfirmedForCurrentSession'];
  projectProfileTrust: ProjectProfileGate['projectProfileTrust'];
  projectProfileGateProfileKey: ProjectProfileGate['projectProfileGateProfileKey'];
  profileProjectMatches: ProjectProfileGate['profileProjectMatches'];
  handleProjectProfileGateConfirm: ProjectProfileGate['handleProjectProfileGateConfirm'];
  profileApplySessionActive: boolean;
  isLiveWizardMappingHandoff: boolean;
  /** Immediate handoff fallback; the durable exemption lives in profile trust. */
  skipSetupReadiness?: boolean;
  dockablePaneVisible: boolean;
  projectIdentityPending: boolean;
  hasResolvedSelectedProfile: boolean;
  objectTypes: ObjectType[];
  menuConfig: MenuConfiguration;
  connectorConfig: ConnectorBindingConfiguration;
  boltConfig: BoltConfiguration;
  processConfig: ProcessPropertiesConfiguration;
  additionalCategoriesConfig: AdditionalCategoriesConfiguration;
  processConditionsEditorState: ScopeEditorPersistenceState;
  projectPropertiesEditorState: ScopeEditorPersistenceState;
}

export interface RepairConsentGateResult {
  mappingReadiness: ReturnType<typeof useMappingProjectReadiness>;
  mappingReadinessEnabled: boolean;
  profileAutoRepairAllowed: boolean;
  profileAutoRepairBlockedMessage: string | null;
  confirmedRepairProfileKey: string | null;
  resolvedProfileMissingPreview: string | null;
  handleRepairSetupConfirm: () => Promise<void>;
  repairConfirmationDialog: ReturnType<typeof useRepairSetupConfirmation>['repairConfirmationDialog'];
  grantRepairConsent: (
    profileKeyOverride?: string,
    options?: { allowWithoutProjectTrust?: boolean }
  ) => void;
  handleProjectProfileGateConfirmWithRepairConsent: () => Promise<boolean>;
}

/**
 * Repair consent + auto-repair gate cluster for the mapping workspace. Owns
 * the current-session consent marker, decides whether automatic parameter
 * repair may run, and wires the readiness hook between them — the gate result
 * feeds `useMappingProjectReadiness`, whose repairable parameter list feeds
 * the repair confirmation dialog, so the three cannot be split across hook
 * calls in the composition root.
 */
export function useRepairConsentGate({
  activeUserId,
  activeProjectSessionKey,
  resolvedProfileKey,
  resolvedProfileName,
  profileConfirmedForCurrentSession,
  projectProfileTrust,
  projectProfileGateProfileKey,
  profileProjectMatches,
  handleProjectProfileGateConfirm,
  profileApplySessionActive,
  isLiveWizardMappingHandoff,
  skipSetupReadiness = false,
  dockablePaneVisible,
  projectIdentityPending,
  hasResolvedSelectedProfile,
  objectTypes,
  menuConfig,
  connectorConfig,
  boltConfig,
  processConfig,
  additionalCategoriesConfig,
  processConditionsEditorState,
  projectPropertiesEditorState,
}: UseRepairConsentGateArgs): RepairConsentGateResult {
  const confirmedRepairProfileKey = useMappingProjectSessionStore(
    (state) => state.repairConsentProfileKey
  );
  const setConfirmedRepairProfileKey = useMappingProjectSessionStore(
    (state) => state.setRepairConsentProfileKey
  );
  const repairConsentProjectTrustExemptionProfileKey = useMappingProjectSessionStore(
    (state) => state.repairConsentProjectTrustExemptionProfileKey
  );
  const confirmProfileForProject = useMappingProjectSessionStore(
    (state) => state.confirmProfileForProject
  );
  useEffect(() => {
    if (
      !profileApplySessionActive &&
      confirmedRepairProfileKey !== null &&
      confirmedRepairProfileKey !== resolvedProfileKey
    ) {
      setConfirmedRepairProfileKey(null);
    }
  }, [
    confirmedRepairProfileKey,
    profileApplySessionActive,
    resolvedProfileKey,
    setConfirmedRepairProfileKey,
  ]);

  // Repair consent (the "Add parameters" dialog or the landing prompt's
  // "Include standard set" answer) is an explicit, current-session,
  // profile-scoped confirmation — strictly stronger than the profile gate
  // confirm, which already records the session confirmation when it loads the
  // profile. Consent without the session confirmation deadlocked the
  // auto-repair gate: the user approved adding the missing parameters, yet
  // `profileConfirmedForCurrentSession` stayed false and the repair never
  // started.
  const handleRepairConsentGranted = useCallback(
    (profileKey: string, options?: { allowWithoutProjectTrust?: boolean }) => {
      if (!profileKey) {
        return;
      }

      setConfirmedRepairProfileKey(profileKey, options);
      if (activeProjectSessionKey) {
        confirmProfileForProject(activeProjectSessionKey, profileKey, 'manual');
      }
    },
    [activeProjectSessionKey, confirmProfileForProject, setConfirmedRepairProfileKey]
  );

  // The gate dialog's acknowledgment checkbox already states that missing
  // parameters will be added to the project, so a successful gate confirm is
  // the repair consent - asking again in the repair popup made the user
  // approve the same mutation twice.
  const handleProjectProfileGateConfirmWithRepairConsent = useCallback(async () => {
    const consentProfileKey = projectProfileGateProfileKey;
    const loaded = await handleProjectProfileGateConfirm();
    if (loaded && consentProfileKey) {
      setConfirmedRepairProfileKey(consentProfileKey);
    }

    return loaded;
  }, [handleProjectProfileGateConfirm, projectProfileGateProfileKey, setConfirmedRepairProfileKey]);

  // Profile read-back proves the configuration was persisted, not that Revit
  // finished creating and saving its required parameters. Only a durable
  // repair-verification record may suppress the restart readiness scan.
  const verifiedSetupBaseline = useUIStore((state) => state.verifiedSetupBaseline);
  const isVerifiedProfileRestart = useMemo(() => {
    if (
      !verifiedSetupBaseline ||
      !resolvedProfileKey ||
      !activeProjectSessionKey ||
      projectProfileTrust?.repairPersistence?.status !== 'saved'
    ) {
      return false;
    }
    // Both keys must be normalized the same way — the baseline identity
    // stores a trimmed-only project key while activeProjectSessionKey is
    // lowercased with slashes normalized via normalizeMappingProjectKey.
    const baselineProjectKey = normalizeMappingProjectKey(verifiedSetupBaseline.identity.projectKey);
    return (
      verifiedSetupBaseline.profileKey === resolvedProfileKey &&
      baselineProjectKey === activeProjectSessionKey
    );
  }, [
    activeProjectSessionKey,
    projectProfileTrust?.repairPersistence?.status,
    resolvedProfileKey,
    verifiedSetupBaseline,
  ]);

  const setupReadinessBypassed =
    skipSetupReadiness ||
    projectProfileTrust?.setupReadinessPolicy === 'legacy-conversion-bypass';

  const mappingReadinessEnabled =
    dockablePaneVisible &&
    !projectIdentityPending &&
    !setupReadinessBypassed &&
    !isVerifiedProfileRestart &&
    (!activeProjectSessionKey || profileConfirmedForCurrentSession);
  const resolvedProfileMatch = profileProjectMatches.matches.find(
    (match) => match.profileKey === resolvedProfileKey
  );
  const resolvedProfileMissingPreview = formatMissingParameterPreview(
    resolvedProfileMatch?.missingParameterNames ?? []
  );
  const { allowed: profileAutoRepairAllowed, blockedMessage: profileAutoRepairBlockedMessage } =
    resolveMappingProfileAutoRepairGate({
      mappingReadinessEnabled,
      hasResolvedSelectedProfile,
      hasProjectProfileTrust: Boolean(projectProfileTrust),
      hasCurrentSessionProjectTrustExemption:
        repairConsentProjectTrustExemptionProfileKey === resolvedProfileKey,
      profileConfirmedForCurrentSession,
      confirmedRepairProfileKey,
      resolvedProfileKey,
      resolvedProfileName,
      missingParameterPreview: resolvedProfileMissingPreview,
    });

  const mappingReadiness = useMappingProjectReadiness({
    activeUserId,
    objectTypes,
    menuConfig,
    connectorConfig,
    boltConfig,
    processConfig,
    additionalCategoriesConfig,
    processConditionsEditorState,
    projectPropertiesEditorState,
    selectedProfileKey: profileConfirmedForCurrentSession || !activeProjectSessionKey
      ? resolvedProfileKey
      : '',
    enabled: mappingReadinessEnabled,
    autoRepair: profileAutoRepairAllowed,
    autoRepairBlockedMessage: profileAutoRepairBlockedMessage,
  });

  // Completing the live wizard already authorizes its scoped setup writes.
  // After connector finalization, the only normal post-save gap is a mapping-
  // only set (including names typed manually in the mapping table). Grant that
  // narrow repair automatically; any connector/bolt/additional/process miss
  // still requires the normal explicit repair confirmation.
  const liveWizardMappingOnlyRepairPending =
    mappingReadinessEnabled &&
    isLiveWizardMappingHandoff &&
    profileConfirmedForCurrentSession &&
    Boolean(resolvedProfileKey) &&
    confirmedRepairProfileKey !== resolvedProfileKey &&
    mappingReadiness.repairAssessmentReady &&
    mappingReadiness.repairableParameterNames.length > 0 &&
    mappingReadiness.mappedParameterNames.length ===
      mappingReadiness.repairableParameterNames.length &&
    mappingReadiness.repairKinds.length === 1 &&
    mappingReadiness.repairKinds[0] === 'mapping';

  useEffect(() => {
    if (!liveWizardMappingOnlyRepairPending) {
      return;
    }

    handleRepairConsentGranted(resolvedProfileKey, { allowWithoutProjectTrust: true });
  }, [
    handleRepairConsentGranted,
    liveWizardMappingOnlyRepairPending,
    resolvedProfileKey,
  ]);

  useEffect(() => {
    if (
      mappingReadinessEnabled &&
      projectProfileTrust &&
      projectProfileTrust.repairPersistence?.status !== 'saved' &&
      profileConfirmedForCurrentSession &&
      mappingReadiness.status === 'ready'
    ) {
      markMappingRepairVerifiedPresent(
        activeUserId,
        activeProjectSessionKey,
        resolvedProfileKey
      );
    }
  }, [
    activeProjectSessionKey,
    activeUserId,
    mappingReadinessEnabled,
    mappingReadiness.status,
    profileConfirmedForCurrentSession,
    projectProfileTrust,
    resolvedProfileKey,
  ]);

  const {
    handleRepairSetupConfirm: requestRepairSetupConfirmation,
    cancelRepairSetupConfirmation,
    repairConfirmationDialog,
  } = useRepairSetupConfirmation({
    resolvedProfileKey,
    repairableParameterNames: mappingReadiness.repairableParameterNames,
    onRepairConfirmed: handleRepairConsentGranted,
  });
  const handleRepairSetupConfirm = useCallback(async () => {
    if (
      profileApplySessionActive ||
      !mappingReadiness.repairAssessmentReady ||
      confirmedRepairProfileKey === resolvedProfileKey
    ) {
      return;
    }

    await requestRepairSetupConfirmation();
  }, [
    confirmedRepairProfileKey,
    mappingReadiness.repairAssessmentReady,
    profileApplySessionActive,
    requestRepairSetupConfirmation,
    resolvedProfileKey,
  ]);
  const promptedRepairScopeRef = useRef<string | null>(null);
  const pendingAutomaticPromptScopeRef = useRef<string | null>(null);
  const repairableParameterSignature = mappingReadiness.repairableParameterNames.join('|');
  const automaticRepairPromptScope =
    mappingReadinessEnabled &&
    activeProjectSessionKey &&
    resolvedProfileKey &&
    profileConfirmedForCurrentSession &&
    projectProfileTrust &&
    confirmedRepairProfileKey !== resolvedProfileKey &&
    !liveWizardMappingOnlyRepairPending &&
    mappingReadiness.repairAssessmentReady &&
    mappingReadiness.status === 'blocked' &&
    mappingReadiness.repairableParameterNames.length > 0 &&
    !mappingReadiness.hasAttemptedRepair &&
    !mappingReadiness.isRepairing &&
    !profileApplySessionActive
      ? `${activeProjectSessionKey}::${resolvedProfileKey}::${repairableParameterSignature}`
      : null;

  useEffect(() => {
    const pendingScope = pendingAutomaticPromptScopeRef.current;
    if (!automaticRepairPromptScope) {
      if (pendingScope) {
        pendingAutomaticPromptScopeRef.current = null;
        cancelRepairSetupConfirmation();
      }
      return;
    }

    if (pendingScope && pendingScope !== automaticRepairPromptScope) {
      pendingAutomaticPromptScopeRef.current = null;
      cancelRepairSetupConfirmation();
    }

    if (promptedRepairScopeRef.current === automaticRepairPromptScope) {
      return;
    }

    promptedRepairScopeRef.current = automaticRepairPromptScope;
    pendingAutomaticPromptScopeRef.current = automaticRepairPromptScope;
    void handleRepairSetupConfirm().finally(() => {
      if (pendingAutomaticPromptScopeRef.current === automaticRepairPromptScope) {
        pendingAutomaticPromptScopeRef.current = null;
      }
    });
  }, [
    automaticRepairPromptScope,
    cancelRepairSetupConfirmation,
    handleRepairSetupConfirm,
  ]);

  // The landing prompt's "Include standard set" answer is an explicit,
  // current-session consent to write the missing standard parameters into the
  // active Revit project — the same consent the repair dialog collects, so it
  // feeds the same gate key.
  const grantRepairConsent = useCallback((
    profileKeyOverride?: string,
    options?: { allowWithoutProjectTrust?: boolean }
  ) => {
    handleRepairConsentGranted(profileKeyOverride?.trim() || resolvedProfileKey, options);
  }, [handleRepairConsentGranted, resolvedProfileKey]);

  return {
    mappingReadiness,
    mappingReadinessEnabled,
    profileAutoRepairAllowed,
    profileAutoRepairBlockedMessage,
    confirmedRepairProfileKey,
    resolvedProfileMissingPreview,
    handleRepairSetupConfirm,
    repairConfirmationDialog,
    grantRepairConsent,
    handleProjectProfileGateConfirmWithRepairConsent,
  };
}
